import { useMemo } from "react";
import { AxiosError } from "axios";
import { useMutation } from "react-query";
import catalog from "@granular/fabric-catalog";

import activities from "../../../redux/activities";
import {
  BatchInvite,
  RawBatchInviteNetworkResponse,
} from "../../../models/invitations";
import { InternalRole } from "../../../models/permissions";
import { MutationEnvelope, ensureMutationState } from "../../../services/query";
import { getAuthToken, getIsAxiosError } from "../../../services/granularAxios";
import invitesService from "../../../services/invites";
import { ensureValue, ensureError } from "../../../services/util";
import { refinedListOfBatchInvites } from "../../../services/helpers/refinements";

import { useMaybeUserId } from "../userId";
import { useMaybeLocale } from "../locale";
import { useMaybeCurrentOperationId } from "../operations";
import { useInvalidateMembers } from "../members";
import { useFeatureFlag, useMaybeFabricFeatureFlag } from "../featureFlags";

import { useInvalidateInvites } from "./invites.invalidate";
import { useSetInvitesGlobalActivityAndStatus } from "./invites.globalStatus";
import { usePollingInvalidationsForUseInviteMembers } from "./invites.mutations.helpers";

// #####################
// ## useInviteMember ##
// #####################

export type PayloadOfUseInviteMember = {
  as: InternalRole;
  withPhoneOrEmail: string;
  inviteLinkUrl?: string;
};
export type ResultOfUseInviteMember = MutationEnvelope<
  PayloadOfUseInviteMember,
  null
>;
export type UseInviteMember = () => ResultOfUseInviteMember;

/**
 * Use the `mutate` function returned from this hook to invite a member to the current operation.
 * This will trigger an invalidation of the invites data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useInviteMember: UseInviteMember = () => {
  const invalidateInvites = useInvalidateInvites();
  const invalidateMembers = useInvalidateMembers();

  const maybeUserId = useMaybeUserId();
  const maybeLocale = useMaybeLocale();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "invites-api",
  });

  const setInvitesGlobalActivityAndStatus =
    useSetInvitesGlobalActivityAndStatus();

  const flagForEnableCustomInviteUrl = useFeatureFlag({
    app: "fabric",
    key: "enable-custom-invite-url",
  });

  const { data, error, status, mutateAsync } = useMutation<
    null,
    AxiosError,
    PayloadOfUseInviteMember
  >(
    async (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const locale = ensureValue(maybeLocale, "locale");
      const operationId = ensureValue(maybeOperationId, "operationId");

      if (catalogFlag) {
        const catalogConfig = {
          // IMPORTANT: We are not passing the authToken as part of the queryKey
          // because we don't want to invalidate our data on auth token change.
          // The auth token can and will change throughout the lifetime of the app,
          // but that should not invalidate our data.
          getAuthToken,
          getLocale: () => locale,
          getOperationId: () => operationId,
        };
        await catalog.invite.postInvitation({
          catalogConfig,
          invite: {
            operationId,
            roleName: payload.as,
            userName: payload.withPhoneOrEmail,
            inviteLinkUrl: flagForEnableCustomInviteUrl
              ? payload.inviteLinkUrl
              : undefined,
          },
        });
      } else {
        await invitesService.inviteMember({
          authToken: getAuthToken(),
          userId,
          operationId,
          locale,
          as: payload.as,
          withPhoneOrEmail: payload.withPhoneOrEmail,
          inviteLinkUrl: flagForEnableCustomInviteUrl
            ? payload.inviteLinkUrl
            : undefined,
        });
      }

      return null;
    },
    {
      onMutate: () => {
        setInvitesGlobalActivityAndStatus({
          activity: activities.inviteMember,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await Promise.all([invalidateInvites(), invalidateMembers()]);

        setInvitesGlobalActivityAndStatus({
          activity: activities.inviteMember,
          status: "success",
        });
      },
      onError: () => {
        setInvitesGlobalActivityAndStatus({
          activity: activities.inviteMember,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// ######################
// ## useInviteMembers ##
// ######################

export type PayloadOfUseInviteMembers = {
  invites: BatchInvite[];
};
export type ResultOfUseInviteMembers = MutationEnvelope<
  PayloadOfUseInviteMembers,
  BatchInvite[]
>;
export type UseInviteMembers = () => ResultOfUseInviteMembers;

/**
 * Use the `mutate` function returned from this hook to invite members to the current operation.
 * This will trigger an invalidation of the invites data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useInviteMembers: UseInviteMembers = () => {
  // NOTE: The logic for this resource is extremely convoluted and possibly somewhat erroneous. I have ported it to
  // React Query as best as I could, preserving what was originally written (and commented) but there is
  // definitely room for improvement here by those that understand the use case of this resource better than I.
  //
  // For the original logic of this functionality, please see:
  // https://gitlab.internal.granular.ag/apollo/fabric/core/-/blob/72c57f7f92beef8d104a740fa61fa218b4f21f3a/src/redux/slices/currentOperation/invites.ts#L120-267.

  const doPollingInvalidations = usePollingInvalidationsForUseInviteMembers();

  const maybeUserId = useMaybeUserId();
  const maybeLocale = useMaybeLocale();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "invites-api",
  });

  const flagForEnableCustomInviteUrl = useFeatureFlag({
    app: "fabric",
    key: "enable-custom-invite-url",
  });

  const setInvitesGlobalActivityAndStatus =
    useSetInvitesGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    BatchInvite[],
    AxiosError,
    PayloadOfUseInviteMembers
  >(async (payload) => {
    setInvitesGlobalActivityAndStatus({
      activity: activities.inviteMembers,
      status: "loading",
    });

    const userId = ensureValue(maybeUserId, "userId");
    const locale = ensureValue(maybeLocale, "locale");
    const operationId = ensureValue(maybeOperationId, "operationId");

    let failedInvites: BatchInvite[] = [];
    try {
      if (catalogFlag) {
        const catalogConfig = {
          // IMPORTANT: We are not passing the authToken as part of the queryKey
          // because we don't want to invalidate our data on auth token change.
          // The auth token can and will change throughout the lifetime of the app,
          // but that should not invalidate our data.
          getAuthToken,
          getLocale: () => locale,
          getOperationId: () => operationId,
        };
        failedInvites = await catalog.invite
          .postInvitations({
            catalogConfig,
            invites: {
              invites: payload.invites.map((invite) => ({
                operationId: invite.cloneAARAccess
                  ? ""
                  : invite.operationId || operationId,
                roleName: invite.cloneAARAccess ? "" : invite.role,
                userName: invite.phoneOrEmail,
                inviteLinkUrl: flagForEnableCustomInviteUrl
                  ? invite.inviteLinkUrl
                  : undefined,
                cloneAARAccess: invite.cloneAARAccess || false,
              })),
            },
          })
          .then((r) => refinedListOfBatchInvites(r.data))
          .catch((_e) => {
            const e = ensureError(_e);
            if (getIsAxiosError(e) && e.response && e.response.status === 400) {
              const failedInvites = refinedListOfBatchInvites(
                e.response.data as RawBatchInviteNetworkResponse,
              );
              return failedInvites;
            }
            throw e;
          });
      } else {
        failedInvites = await invitesService.inviteMembers({
          authToken: getAuthToken(),
          userId,
          operationId,
          locale,
          invites: payload.invites.map((invite) => ({
            operationId: invite.cloneAARAccess
              ? ""
              : invite.operationId || operationId,
            roleName: invite.cloneAARAccess ? "" : invite.role,
            userName: invite.phoneOrEmail,
            inviteLinkUrl: flagForEnableCustomInviteUrl
              ? invite.inviteLinkUrl
              : undefined,
            cloneAARAccess: invite.cloneAARAccess || false,
          })),
        });
      }
    } catch (_e) {
      const e = ensureError(_e);
      /**
       * If the call bombs with an HTTP 500 or higher, error and throw.
       * NOTE: Do not do this on HTTP 400!
       */
      if (getIsAxiosError(e) && e.response && e.response.status >= 500) {
        setInvitesGlobalActivityAndStatus({
          activity: activities.inviteMembers,
          status: "error",
        });
        throw e;
      }
    }

    doPollingInvalidations({ payload, failedInvites });

    /**
     * Consider this a success only if there are no failed invites.
     */
    if (failedInvites.length === 0) {
      setInvitesGlobalActivityAndStatus({
        activity: activities.inviteMembers,
        status: "success",
      });
    }

    // According to the original implementation, at this point, we are neither success nor error?
    // We will allow React Query to treat this as a success, because the comments in the error handling above
    // clearly state not to throw an error otherwise.

    return failedInvites;
  });

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};

// #######################
// ## useUnInviteMember ##
// #######################

export type PayloadOfUseUnInviteMember = {
  emailAddressOrPhone: string;
};
export type ResultOfUseUnInviteMember = MutationEnvelope<
  PayloadOfUseUnInviteMember,
  null
>;
export type UseUnInviteMember = () => ResultOfUseUnInviteMember;

/**
 * Use the `mutate` function returned from this hook to un-invite a member to the current operation.
 * This will trigger an invalidation of the invites data.
 *
 * The `data`, `error`, and `status` properties returned from this hook will reflect
 * the state of the mutation.
 */
export const useUnInviteMember: UseUnInviteMember = () => {
  const invalidateInvites = useInvalidateInvites();
  const invalidateMembers = useInvalidateMembers();

  const maybeUserId = useMaybeUserId();
  const maybeLocale = useMaybeLocale();
  const maybeOperationId = useMaybeCurrentOperationId();
  const catalogFlag = useMaybeFabricFeatureFlag({
    key: "invites-api",
  });

  const setInvitesGlobalActivityAndStatus =
    useSetInvitesGlobalActivityAndStatus();

  const { data, error, status, mutateAsync } = useMutation<
    null,
    AxiosError,
    PayloadOfUseUnInviteMember
  >(
    async (payload) => {
      const userId = ensureValue(maybeUserId, "userId");
      const locale = ensureValue(maybeLocale, "locale");
      const operationId = ensureValue(maybeOperationId, "operationId");

      if (catalogFlag) {
        const catalogConfig = {
          // IMPORTANT: We are not passing the authToken as part of the queryKey
          // because we don't want to invalidate our data on auth token change.
          // The auth token can and will change throughout the lifetime of the app,
          // but that should not invalidate our data.
          getAuthToken,
          getLocale: () => locale,
          getOperationId: () => operationId,
        };
        await catalog.invite.deleteInvitations({
          catalogConfig,
          operationId,
          username: payload.emailAddressOrPhone,
        });
      } else {
        await invitesService.removeInvitation({
          authToken: getAuthToken(),
          userId,
          operationId,
          locale,
          username: payload.emailAddressOrPhone,
        });
      }

      return null;
    },
    {
      onMutate: () => {
        setInvitesGlobalActivityAndStatus({
          activity: activities.unInviteMember,
          status: "loading",
        });
      },
      onSuccess: async () => {
        await Promise.all([invalidateInvites(), invalidateMembers()]);

        setInvitesGlobalActivityAndStatus({
          activity: activities.unInviteMember,
          status: "success",
        });
      },
      onError: () => {
        setInvitesGlobalActivityAndStatus({
          activity: activities.unInviteMember,
          status: "error",
        });
      },
    },
  );

  const mutate = mutateAsync;

  return useMemo(
    () => ({
      ...ensureMutationState({ data, error, status }),
      mutate,
    }),
    [data, error, status, mutate],
  );
};
